<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import SelectInput from "@kontextwerk/web-sdk/ui/forms/SelectInput.svelte"
  import { MAX_PREPARATION_DURATION_MINUTES, type WorkOrderCreationFields } from "../../../../_shared/_logic/work-order-creation"
  import { formatDateOnly } from "../../../../../../../../../../../../_shared/_logic/dates"
  import { SCHEDULING_MINUTE_STEP } from "../../../../../../../../../../../../../../_shared/_logic/time-granularity"
  import {
    CUSTOM_ORDER_TYPE_SELECTION,
    isCustomOrderTypeSelection,
    orderTypeSelectionValues,
    reconcileOrderTypeSelection,
    summedTypicalDurationMinutes,
  } from "../../../../../../../../../../../../_shared/_logic/order-type-selection"

  const {
    column,
    value,
    rowId,
    rowNumber,
    orderTypes,
    employeeOptions,
    disabled = false,
    onChange,
  }: {
    column: string
    value: WorkOrderCreationFields
    rowId: string
    rowNumber: number
    orderTypes: OrderType[]
    employeeOptions: Array<{ label: string; value: string }>
    disabled?: boolean
    onChange: (value: WorkOrderCreationFields) => void
  } = $props()
  const orderTypeOptions = $derived([
    ...[...orderTypes]
      .sort((a, b) =>
        a.internal_label.localeCompare(b.internal_label, "de", { sensitivity: "base" }),
      )
      .map((type) => ({ label: type.internal_label, value: type.id })),
    { label: "Sonstige (freier Text)", value: CUSTOM_ORDER_TYPE_SELECTION },
  ])
  const selectedTypes = $derived(orderTypeSelectionValues(value))
  const changeTypes = (next: string[]) => {
    if (disabled) return

    const selection = reconcileOrderTypeSelection(selectedTypes, next)

    if (isCustomOrderTypeSelection(selection)) {
      onChange({
        ...value,
        order_type_ids: [],
        custom_order_type: value.custom_order_type ?? { internal_label: "", public_label: "" },
      })
      return
    }

    onChange({
      ...value,
      order_type_ids: selection,
      custom_order_type: null,
      estimated_duration_min: String(summedTypicalDurationMinutes(selection, orderTypes) ?? ""),
    })
  }
</script>

<div class="order-field-cell form light">
  {#if column === "order"}
    <SelectInput
      clearable={false}
      id={`batch-order-type-${rowId}`}
      ariaLabel={`Auftragstyp für Zeile ${rowNumber}`}
      placeholder="Auftragstyp wählen"
      options={orderTypeOptions}
      value={selectedTypes}
      multiple={true}
      surfaceTone="dark"
      {disabled}
      onValueChange={(next) =>
        changeTypes(Array.isArray(next) ? next.map(String) : next ? [String(next)] : [])}
    />
    {#if isCustomOrderTypeSelection(selectedTypes)}
      <Input
        id={`batch-order-type-custom-${rowId}`}
        placeholder={`Auftragstyp (freier Text) für Zeile ${rowNumber}`}
        labelVisibility="hidden"
        surfaceTone="dark"
        {disabled}
        bind:value={
          () => value.custom_order_type?.internal_label ?? "",
          (next) => {
            if (!disabled)
              onChange({
                ...value,
                custom_order_type: {
                  internal_label: String(next ?? ""),
                  public_label: String(next ?? ""),
                },
              })
          }
        }
      />
    {/if}
  {:else if column === "duration"}
    <Input
      id={`batch-order-duration-${rowId}`}
      type="number"
      placeholder={`Dauer (Minuten) für Zeile ${rowNumber}`}
      labelVisibility="hidden"
      min={SCHEDULING_MINUTE_STEP}
      max={MAX_PREPARATION_DURATION_MINUTES}
      step={SCHEDULING_MINUTE_STEP}
      surfaceTone="dark"
      {disabled}
      bind:value={
        () => value.estimated_duration_min,
        (next) => {
          const duration = String(next ?? "")

          if (!disabled && duration !== value.estimated_duration_min)
            onChange({ ...value, estimated_duration_min: duration })
        }
      }
    />
  {:else if column === "employees"}
    <SelectInput
      clearable={false}
      id={`batch-order-employees-${rowId}`}
      ariaLabel={`Zugelassene Mitarbeitende für Zeile ${rowNumber}`}
      placeholder="Mitarbeitende wählen"
      options={employeeOptions}
      value={value.allowed_employee_ids}
      multiple={true}
      surfaceTone="dark"
      {disabled}
      onValueChange={(next) => {
        if (!disabled) onChange({ ...value, allowed_employee_ids: Array.isArray(next) ? next : [] })
      }}
    />
  {:else if column === "due"}
    <Input
      id={`batch-order-due-${rowId}`}
      type="date"
      placeholder={`Frist für Zeile ${rowNumber}`}
      labelVisibility="hidden"
      surfaceTone="dark"
      {disabled}
      bind:value={
        () => value.due_date,
        (next) => {
          const dueDate = formatDateOnly(next)

          if (!disabled && dueDate !== value.due_date) onChange({ ...value, due_date: dueDate })
        }
      }
    />
  {/if}
</div>

<style>
  .order-field-cell {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 0.3rem;
    min-height: 54px;
    width: 100%;
    min-width: 0;
  }
  .order-field-cell :global(.svelte-select) {
    --height: 48px;
    --padding: 0 !important;
    --selected-item-padding: 0 !important;
  }
  .order-field-cell :global(.kw-multi-select) {
    height: 48px;
    min-height: 48px;
  }
  .order-field-cell :global(label) {
    display: flex;
    align-items: flex-end;
    height: 48px;
    min-height: 48px;
    max-height: none;
  }
  .order-field-cell :global(label > input) {
    box-sizing: border-box;
    height: 48px !important;
    padding: 0 !important;
  }
  .order-field-cell :global(.date-input) {
    box-sizing: border-box;
    height: 48px;
    padding-block: 0;
    grid-template-columns: auto minmax(0, 1fr) auto;
  }
  .order-field-cell :global(.date-input__field) {
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 0 !important;
  }
  .order-field-cell :global(label),
  .order-field-cell :global(.select-input-shell) {
    width: 100%;
    min-width: 0;
    margin: 0;
  }
</style>
